import React from 'react';

import { Box, InputAdornment, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import { Button, FullscreenLoader } from 'src/components/common';
import { NumberField } from 'src/components/finalForm';
import { useInitialBillingRules } from 'src/hooks/account/useInitialBillingRules';
import { useNotification } from 'src/hooks/app';
import { SxProp } from 'src/types/mui';
import { validateWithYupSchema } from 'src/utils/yup';

import { VerifierBillingRulesFormFields, VerifierBillingRulesFormFieldsValues } from './types';
import { getVerifierInitialBillingRulesInitialValues } from './utils/getInitialBillingInitialValues';
import { VerifierInitialBillingRulesValidationSchema } from './utils/validation';

export const VerifierInitialBillingRules: React.FC = () => {
  const {
    initialBillingRules,
    updateInitialBillingRules,
    updating,
    loading: isInitialBillingRulesLoading,
  } = useInitialBillingRules();
  const { createApolloContext } = useNotification();
  const initialValues = getVerifierInitialBillingRulesInitialValues(initialBillingRules);

  const onSubmit = React.useCallback(
    async (data: VerifierBillingRulesFormFieldsValues) => {
      try {
        await updateInitialBillingRules({
          data,
          context: createApolloContext({
            successMessage: 'The changes have been saved',
          }),
        });
      } catch (error) {
        console.error(error);
      }
    },
    [createApolloContext, updateInitialBillingRules],
  );

  const loading = updating || isInitialBillingRulesLoading;

  if (loading) {
    return <FullscreenLoader />;
  }

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      validate={validateWithYupSchema(VerifierInitialBillingRulesValidationSchema)}
    >
      {({ form, dirty, submitting, valid }) => (
        <React.Fragment>
          <Box mb={4}>
            <Typography fontSize="16px" variant="subtitle1">
              Verification of Income (VoI)
            </Typography>
            <Typography fontSize="16px" color="text.secondary" mb="19px" variant="body1">
              Amount paid for each Verification of Income Report (VoI). Changes will be applied
              instantly.
            </Typography>
            <Field
              name={VerifierBillingRulesFormFields.voI}
              fullWidth
              required
              label="Verification of Income"
              component={NumberField}
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={inputAdornmentStyles}>
                    $
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box mb={4}>
            <Typography fontSize="16px" variant="subtitle1">
              Verification of Employment (VoE)
            </Typography>
            <Typography fontSize="16px" color="text.secondary" variant="body1">
              Amount paid for each Verification of Employment and Income Report (VoE).
            </Typography>
            <Typography fontSize="16px" color="text.secondary" mb="19px">
              Changes will be applied instantly.
            </Typography>
            <Field
              name={VerifierBillingRulesFormFields.voE}
              fullWidth
              required
              label="Verification of Employment"
              component={NumberField}
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={inputAdornmentStyles}>
                    $
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box>
            <Typography fontSize="16px" variant="subtitle1">
              Reverification
            </Typography>
            <Typography fontSize="16px" color="text.secondary" variant="body1">
              Amount paid for each Reverification Report, for previously requested employees.
            </Typography>
            <Typography fontSize="16px" color="text.secondary" mb="19px">
              Changes will be applied instantly.
            </Typography>
            <Field
              name={VerifierBillingRulesFormFields.reverification}
              fullWidth
              required
              label="Reverification"
              component={NumberField}
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={inputAdornmentStyles}>
                    $
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box display="flex" justifyContent="center" alignItems="center" gap="24px" mt={5}>
            <Button
              color="error"
              disabled={!dirty}
              size="medium"
              type="reset"
              variant="outlined"
              onClick={() => form.restart()}
            >
              reset
            </Button>
            <Button
              color="primary"
              disabled={!valid || !dirty || submitting}
              loading={submitting}
              variant="contained"
              type="submit"
            >
              save changes
            </Button>
          </Box>
        </React.Fragment>
      )}
    </Form>
  );
};

const inputAdornmentStyles: SxProp = {
  '.MuiTypography-root': {
    fontWeight: 600,
  },
};
